<template>
    <app-layout title="Credit Request">

        <page-header>
            <template #title>Credit Request</template>
        </page-header>

        <div v-if="selectedRefunds.length > 0" class="px-8 mb-4 flex gap-3">
            <jet-button
                @click="confirmMassApprove"
                :disabled="processing"
            >
                 Approve Selected
            </jet-button>

            <jet-danger-button
                @click="confirmMassReject"
                :disabled="processing"
            >
                Reject Selected
            </jet-danger-button>
        </div>

        <div class="px-8">
            <div class="flex">
                <form class="flex flex-1 gap-x-2 flex-wrap" @submit.prevent="handleSubmit">
                    <!-- Search / Filters -->
                    <div class="flex flex-1 gap-x-2">
                        <div class="flex flex-col flex-1">
                            <jet-label for="date_range" value="Date Range" />
                            <datepicker
                                inputClassName="dp-custom-input"
                                @update:modelValue="handleSubmit"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart="0"
                            />
                        </div>

                        <div class="flex flex-col flex-1 min-w-36">
                            <jet-label for="status" value="Status" />
                            <multiselect class="multi-sm min-w-36 h-7 text-base"
                                v-model="form.status"
                                :options="statusOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1 min-w-36">
                            <jet-label for="type" value="Type" />
                            <multiselect class="multi-sm min-w-36 h-7 text-base"
                                v-model="form.type"
                                :options="typeOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="search" value="Search" />
                            <jet-input type="text" class="h-7 text-base" name="search" v-model="form.search" @input="handleSubmit" placeholder="Search..."/>
                        </div>
                    </div>
                </form>

                <div class="self-end">
                    <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>
            </div>
        </div>

        <div class="px-8 pt-2 pb-4">
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <th class="px-4 py-2"><jet-checkbox v-model:checked="allSelected" @change="toggleSelectAll"/></th>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="id" :direction="sort_direction" :column="sort_column">#</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="order_id" :direction="sort_direction" :column="sort_column">Order #</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="name" :direction="sort_direction" :column="sort_column">Customer</sortable-header>
                            <th class="text-right"></th>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="amount" :direction="sort_direction" :column="sort_column">Order Amount</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="amount" :direction="sort_direction" :column="sort_column">Refund Amount</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="status" :direction="sort_direction" :column="sort_column">Status</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="submitted_by" :direction="sort_direction" :column="sort_column">Submitted By</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="created_at" :direction="sort_direction" :column="sort_column">Submitted Date</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="reviewed_by" :direction="sort_direction" :column="sort_column">Reviewed By</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="reviewed_date" :direction="sort_direction" :column="sort_column">Reviewed Date</sortable-header>
                            <th class="text-left"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="refund in refunds.data" :key="refund.id">
                            <td class="px-4 py-2">
                                <div class="flex justify-center items-center">
                                    <jet-checkbox
                                        :value="refund.id"
                                        v-model:checked="selectedRefunds"
                                        :disabled="!refund.order.is_wholesale || refund.status !== 'new'"
                                    />
                                </div>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ refund.id }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ refund.order.order_number }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ orderVisitDisplayName(refund.order) }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    <Pill v-if="isMilkCrateReturn(refund)" color="blue" size="xs">
                                        Milk Crate Return
                                    </Pill>
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ toCurrency(refund.order.charged_total) }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ toCurrency(parseFloat(refund.amount)) }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ refund.statusDef }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ refund.submitted_by?.name || '' }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ formatDate(refund.created_at) }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ refund.reviewed_by?.name }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('refunds.show', refund.id)">
                                    {{ formatDate(refund.reviewed_date) }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <jet-tertiary-button
                                    v-if="refund.status === 'rejected' || refund.status === 'refunded' || refund.status === 'closed_no_refund'"
                                    :href="route('refunds.edit', refund.id)"
                                    class="text-sm"
                                >
                                    Edit
                                </jet-tertiary-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <pagination class="inline-flex" :links="refunds.links" />

            <!-- Approve Modal -->
            <jet-confirmation-modal :show="showApproveModal" @close="showApproveModal = false">
                <template #title>
                    Approve Refunds
                </template>

                <template #content>
                    Are you sure you want to approve
                    <strong>{{ selectedRefunds.length }}</strong>
                    refund<span v-if="selectedRefunds.length !== 1">s</span>?
                </template>

                <template #footer>
                    <jet-button @click="approveSelected">
                        Yes, Approve
                    </jet-button>

                    <jet-tertiary-button class="ml-2" @click="showApproveModal = false">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
            <!-- Reject Modal -->
            <jet-confirmation-modal :show="showRejectModal" @close="showRejectModal = false">
                <template #title>
                    Reject Refunds
                </template>

                <template #content>
                    <div class="mb-2">
                        Reject <strong>{{ selectedRefunds.length }}</strong>
                        refund<span v-if="selectedRefunds.length !== 1">s</span>.
                    </div>

                    <jet-label value="Rejection Note" />
                    <textarea v-model="rejectNote" class="w-full border rounded p-2 mt-1" rows="3"></textarea>
                </template>

                <template #footer>
                    <jet-button @click="rejectSelected" :disabled="!rejectNote.length">
                        Reject
                    </jet-button>

                    <jet-tertiary-button class="ml-2" @click="showRejectModal = false">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
        </div>
    </app-layout>
</template>

<script>
import { useForm, Link } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal.vue';
import JetCheckbox from '@/Jetstream/Checkbox';
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import JetDialogModal from '@/Jetstream/DialogModal.vue'
import { DateTime } from "luxon";
import Pagination from '@/Jetstream/Pagination';
import Datepicker from '@vuepic/vue-datepicker';
import Multiselect from '@vueform/multiselect';
import JetDropdown from '@/Jetstream/Dropdown';
import SortableHeader from '@/Shared/SortableHeader';
import Pill from '@/Jetstream/Pill.vue';
import { orderVisitDisplayName } from '@/helper';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    setup(props) {
        const form = useForm({
            search: props.filters.search,
            from: props.filters.from,
            to: props.filters.to,
            reviewed_by: props.filters.reviewed_by,
            status: props.filters.status,
            type: props.filters.type,
            sort_column: props.sort_column,
            sort_direction: props.sort_direction,
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetDangerButton,
        JetInput,
        JetLabel,
        JetCheckbox,
        JetTertiaryButton,
        JetConfirmationModal,
        JetDialogModal,
        Link,
        Pagination,
        Datepicker,
        Multiselect,
        JetDropdown,
        SortableHeader,
        Pill,
    },

    props: ['refunds', 'filters', 'sort_column', 'sort_direction'],

    data() {
        return {
            date: null,

            selectedRefunds: [],
            allSelected: false,
            processing: false,

            showApproveModal: false,
            showRejectModal: false,
            rejectNote: '',
            
            statusOptions: [
                { value: 'new', label: 'New' },
                { value: 'refunded', label: 'Refunded' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'closed_no_refund', label: 'Closed – no refund' },
            ],

            typeOptions: [
                { value: 'milk_crate_return', label: 'Milk Crate Return' },
                { value: 'other', label: 'Other' },
            ],
        }
    },

    methods: {
        orderVisitDisplayName,

        toggleSelectAll() {
            this.allSelected
                ? this.selectedRefunds = [...this.wholesaleRefundIds]
                : this.selectedRefunds = [];
        },

        confirmMassApprove() {
            this.showApproveModal = true;
        },

        confirmMassReject() {
            this.rejectNote = '';
            this.showRejectModal = true;
        },

        approveSelected() {
            this.showApproveModal = false;
            this.submitMassAction('refunds.mass-approve');
        },

        rejectSelected() {
            this.showRejectModal = false;
            this.submitMassAction('refunds.mass-reject', {
                reviewed_note: this.rejectNote
            });
        },

        submitMassAction(routeName, payload = {}) {
            this.processing = true;

            axios.post(route(routeName), {
                refund_ids: this.selectedRefunds,
                ...payload,
            })
            .then(response => {
                // Reset
                this.selectedRefunds = [];
                this.allSelected = false;

                // Reload refund table only
                this.$inertia.reload({ only: ['refunds'] });
            })
            .catch(error => {
                console.error(error);
                alert('An error occurred while processing the request.');
            })
            .finally(() => {
                this.processing = false;
            });
        },

        handleSubmit: _.debounce(function () {
            this.form
                .transform((data) => ({
                    ...data,
                    from: this.date ? DateTime.fromJSDate(this.date[0]).toISODate() : null,
                    to: this.date ? DateTime.fromJSDate(this.date[1]).toISODate() : null,
                }))
                .get(route('refunds.index'), { preserveState: true });
        }, 250),

        handleSort (sort) {
            this.form.sort_column = sort.column;
            this.form.sort_direction = sort.direction;
        },

        handleReset () {
            this.date = null;
            this.form.from = null;
            this.form.to = null;
            this.form.search = '';
            this.form.reviewed_by = null;
            this.form.status = null;
            this.form.type = null;
            this.form.sort_column = 'created_at';
            this.form.sort_direction = 'desc';

            this.handleSubmit();
        },

        setDateRange() {
            if(this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        formatDate (date) {
            if (!date) {
                return null;
            }
            const parsedDate = new Date(date);

            return DateTime.fromISO(parsedDate.toISOString()).toFormat('MMM d, yyyy');
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if(start && end)
            {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        },

        formatTime(timestamp) {
            let date = DateTime.fromSQL(timestamp).invalid ? DateTime.fromISO(timestamp) : DateTime.fromSQL(timestamp);

            if (date.c.hour === 0 && date.c.minute === 0 && date.c.second === 0) {
                return '';
            }

            return date.toFormat('h:mm a');
        },

        reloadPage() {
            this.$inertia.reload({ only: ['refunds'] });
        },

        isMilkCrateReturn(refund) {
            const items = refund?.refund_order_items || [];

            return items.every(item => item.product_id === 3588);
        }
    },

    mounted() {
        this.setDateRange();
    },

    computed: {
        wholesaleRefundIds() {
            if (!this.refunds || !this.refunds.data) return [];
            return this.refunds.data
                .filter(r => r.order && r.order.is_wholesale && r.status === 'new')
                .map(r => r.id);
        },
    },

    watch: {
        "form.sort_column": {
            handler: function() {
                this.handleSubmit();
            }
        },

        "form.sort_direction": {
            handler: function() {
                this.handleSubmit();
            }
        },
    },
}
</script>
<style>
.dp-custom-input {
    @apply h-7;
}
</style>
